import { useMemo, useRef, useState } from "react";
import { trendBuckets } from "@/lib/utils/daylight-insight/compute";
import { granularity, type Granularity } from "@/lib/utils/daylight-insight/dates";
import { fmtVal, axisFmt, unitLabel } from "@/lib/utils/daylight-insight/format";
import type { Bucket, DateRange, Metric, MetricKind } from "@/lib/utils/daylight-insight/types";

interface TrendBarsProps {
  metric: Metric;
  range: DateRange;
  wide?: boolean;
}
export function TrendBars({ metric, range, wide }: TrendBarsProps) {
  const buckets = useMemo(() => trendBuckets(metric, range).slice(-6), [metric, range]);
  const vals = buckets.map((b) => b.value);
  const min = Math.min(...vals);
  // The 1-floor stops a flat near-zero series filling the sparkline, but it must
  // not apply to a genuinely negative one (a debit metric such as Revenue
  // Adjustments): forcing the top of the scale to 1 there flattens every bar.
  const max = min < 0 ? Math.max(...vals) : Math.max(...vals, 1);
  const span = max - min || 1;
  const last = vals[vals.length - 1];
  let color = "var(--chart-primary)";
  if (metric.benchmark != null) {
    const beats = metric.lowerBetter ? last <= metric.benchmark : last >= metric.benchmark;
    color = beats ? "var(--green)" : "var(--red)";
  }
  const n = vals.length, W = wide ? 120 : 62, H = wide ? 22 : 26, slot = W / n, bw = slot * 0.6;
  return (
    <svg width={wide ? "100%" : 62} height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio={wide ? "none" : "xMidYMid meet"} style={{ display: "block" }}>
      {vals.map((v, i) => {
        const h = 3 + ((v - min) / span) * (H - 5);
        return <rect key={i} x={i * slot + (slot - bw) / 2} y={H - h} width={bw} height={h} rx="1.5" fill={color} opacity={i === n - 1 ? 1 : 0.28} />;
      })}
    </svg>
  );
}

interface TrendChartProps {
  metric: Metric;
  range: DateRange;
  height?: number;
}
export function TrendChart({ metric, range, height = 244 }: TrendChartProps) {
  const buckets = useMemo(() => trendBuckets(metric, range), [metric, range]);
  return <TrendChartView metric={metric} buckets={buckets} gran={granularity(range)} height={height} />;
}

interface TrendChartViewProps {
  metric: Metric;
  buckets: Bucket[];
  gran?: Granularity;
  height?: number;
  /** Explicit line/point colour (a CSS var). When given it overrides the
   *  benchmark-based green/red so callers with no numeric target (the API trends
   *  view) can colour by trend direction instead — matching the KPI sparkline. */
  color?: string;
}
/** The trend line chart rendered from explicit `buckets` — lets callers plot a
 *  live API series (or the seeded one) with identical styling. */
export function TrendChartView({ metric, buckets, gran = "month", height = 244, color }: TrendChartViewProps) {
  const g = gran;
  const [hover, setHover] = useState<number | null>(null);
  const W = 520, H = height, padL = 44, padR = 16, padT = 16, padB = 30;
  const vals = buckets.map((b) => b.value);
  let max = Math.max(...vals, metric.benchmark ?? -Infinity);
  let min = Math.min(...vals, metric.benchmark ?? Infinity);
  // A series can legitimately sit below zero — Revenue Adjustments is a debit to
  // revenue, so every period is negative. Only the 12% of HEADROOM may be cut
  // off at zero, and only for a metric whose data never actually goes negative:
  // clamping a real negative series onto a 0-based axis put its whole line
  // outside the plot, which is what left the chart blank.
  const dataMin = min;
  const pad = (max - min) * 0.12 || 1; max += pad; min -= pad;
  if (min < 0 && dataMin >= 0) min = 0;
  const span = max - min || 1;
  const x = (i: number) => padL + (i / (buckets.length - 1)) * (W - padL - padR);
  const y = (v: number) => padT + (1 - (v - min) / span) * (H - padT - padB);
  const pts = buckets.map((b, i) => `${x(i)},${y(b.value)}`).join(" ");
  const last = vals[vals.length - 1];
  let lineColor = color ?? "var(--chart-primary)";
  if (!color && metric.benchmark != null) lineColor = (metric.lowerBetter ? last <= metric.benchmark : last >= metric.benchmark) ? "var(--green)" : "var(--red)";
  // Which ticks get a label is a question of WIDTH, not of grain: a twelve-point
  // axis has ~42px per tick, so "Jun '25" fits and "Jul 20 '26" does not. SVG
  // can't measure text, so the widest label is approximated from its glyph count
  // (sans ≈ 0.58em/char) and every other tick is dropped only when they would
  // actually collide. Previously this keyed off the grain, which thinned a daily
  // axis that had room and crowded one that didn't.
  const fs = g === "month" ? 9 : 8;
  const slot = (W - padL - padR) / Math.max(buckets.length - 1, 1);
  const widest = Math.max(1, ...buckets.map((b) => b.label.length)) * fs * 0.58;
  const showLabel = (i: number) => widest <= slot || i % 2 === 0;
  // persistent value labels at the points that matter: current, peak, low
  const iMax = vals.indexOf(Math.max(...vals)), iMin = vals.indexOf(Math.min(...vals)), iLast = vals.length - 1;
  const labelIdx = [...new Set([iLast, iMax, iMin])];
  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: "block" }} onMouseLeave={() => setHover(null)}>
      {[0, 0.25, 0.5, 0.75, 1].map((t) => {
        const v = min + t * span;
        return <g key={t}>
          <line x1={padL} x2={W - padR} y1={y(v)} y2={y(v)} stroke="var(--line-soft)" strokeWidth="1" />
          <text x={padL - 6} y={y(v) + 3} textAnchor="end" fontSize="9" fill="var(--ink-3)">{fmtVal(metric, v).replace(/\.\d+/, (m) => metric.kind === "currency" ? m : "")}</text>
        </g>;
      })}
      {/* Zero baseline — only drawn when the series crosses it, so a chart that
          runs negative says WHERE negative starts instead of looking like a
          plain descent. */}
      {min < 0 && max > 0 && (
        <line x1={padL} x2={W - padR} y1={y(0)} y2={y(0)} stroke="var(--ink-4)" strokeWidth="1" />
      )}
      {metric.benchmark != null && (
        <line x1={padL} x2={W - padR} y1={y(metric.benchmark)} y2={y(metric.benchmark)} stroke="var(--ink-3)" strokeWidth="1" strokeDasharray="4 4" />
      )}
      <polyline points={pts} fill="none" stroke={lineColor} strokeWidth="2.2" strokeLinejoin="round" strokeLinecap="round" />
      {buckets.map((b, i) => (
        <g key={i}>
          {showLabel(i) && <text x={x(i)} y={H - 10} textAnchor="middle" fontSize={fs} fill="var(--ink-3)">{b.label}</text>}
          <circle cx={x(i)} cy={y(b.value)} r={i === buckets.length - 1 ? 3.6 : 2.4} fill={lineColor} />
          <rect x={x(i) - (W / buckets.length) / 2} y={padT} width={W / buckets.length} height={H - padT - padB} fill="transparent" onMouseEnter={() => setHover(i)} />
        </g>
      ))}
      {hover == null && labelIdx.map((i) => {
        const txt = fmtVal(metric, vals[i]);
        // SVG can't measure text, so approximate the pill from the glyph count
        // (700-weight 10.5px sans ≈ 6.4px/char) plus horizontal padding.
        const bw = txt.length * 6.4 + 12, bh = 17, gap = 9;
        const py = y(vals[i]);
        // Peak sits above its point, trough below — that keeps the pill on the
        // empty side of the line. Flip either way if it would leave the plot.
        let up = i !== iMin;
        if (up && py - gap - bh < padT) up = false;
        if (!up && py + gap + bh > H - padB) up = true;
        const cy = py + (up ? -gap - bh / 2 : gap + bh / 2);
        // Clamp so edge labels stay inside the axes instead of being clipped.
        const cx = Math.min(Math.max(x(i), padL + bw / 2), W - padR - bw / 2);
        return (
          <g key={"lab" + i} style={{ pointerEvents: "none" }}>
            <rect x={cx - bw / 2} y={cy - bh / 2} width={bw} height={bh} rx="5"
              fill="var(--surface-1)" fillOpacity="0.94" stroke="var(--line)" strokeWidth="1" />
            <text x={cx} y={cy + 3.8} textAnchor="middle" fontSize="10.5" fontWeight="700" fill="var(--ink-1)">{txt}</text>
          </g>
        );
      })}
      {hover != null && (
        <g>
          <line x1={x(hover)} x2={x(hover)} y1={padT} y2={H - padB} stroke="var(--ink-4)" strokeWidth="1" />
          <rect x={Math.min(x(hover) + 8, W - 90)} y={padT} width="82" height="34" rx="6" fill="#fff" stroke="var(--line)" />
          <text x={Math.min(x(hover) + 8, W - 90) + 8} y={padT + 14} fontSize="9" fill="var(--ink-3)">{buckets[hover].label}</text>
          <text x={Math.min(x(hover) + 8, W - 90) + 8} y={padT + 28} fontSize="12" fontWeight="600" fill="var(--ink-1)">{fmtVal(metric, buckets[hover].value)}</text>
        </g>
      )}
    </svg>
  );
}

export interface CompareSeries {
  id?: string;
  key: string;
  color: string;
  kind: MetricKind;
  /** A null value marks a period the series has no data for — the line skips it
   *  (real API trends can be sparse across the shared period axis). */
  buckets: { label: string; value: number | null }[];
}

// series: up to 2 distinct kinds → left + right axes.
export function CompareChart({ series }: { series: CompareSeries[] }) {
  const W = 540, H = 268, padL = 52, padR = 52, padT = 16, padB = 34;
  const [hoverI, setHoverI] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const kinds = [...new Set(series.map((s) => s.kind))].slice(0, 2);
  const groups = kinds.map((k) => {
    const vals = series.filter((s) => s.kind === k)
      .flatMap((s) => s.buckets.map((b) => b.value))
      .filter((v): v is number => v != null);
    let max = Math.max(...vals), min = Math.min(...vals);
    const dataMin = min;
    const pad = (max - min) * 0.12 || 1; max += pad; min -= pad;
    // Hybrid Y-axis: counts and currency anchor to 0; rates/durations stay padded.
    // Either anchor applies only to a series that never goes negative — a debit
    // metric (Revenue Adjustments) has to keep its negative axis, or its line
    // falls below the plot and the compare chart draws nothing for it.
    if (dataMin >= 0) {
      if (k === "count" || k === "currency") min = 0;
      else if (min < 0) min = 0;
    }
    return { kind: k, min, max, span: (max - min) || 1, fmt: axisFmt(k) };
  });
  const gOf = (s: CompareSeries) => groups.find((g) => g.kind === s.kind) || groups[0];
  const len = series[0]?.buckets.length || 12;
  const x = (i: number) => padL + (i / Math.max(len - 1, 1)) * (W - padL - padR);
  const y = (s: CompareSeries, v: number) => { const g = gOf(s); return padT + (1 - (v - g.min) / g.span) * (H - padT - padB); };
  const showX = (i: number) => (len <= 7 ? true : i % 2 === 0);
  const left = groups[0], right = groups[1];
  const onMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const svg = svgRef.current; if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const px = ((e.clientX - rect.left) / rect.width) * W;
    const i = Math.max(0, Math.min(len - 1, Math.round(((px - padL) / (W - padL - padR)) * (len - 1))));
    setHoverI(i);
  };
  const tipX = hoverI != null ? x(hoverI) : 0;
  const tipRight = hoverI != null && tipX > W * 0.6;
  // End-of-line value pills, matching the trend chart: SVG can't measure text,
  // so the box is approximated from the glyph count (700-weight 10.5px sans
  // ≈ 6.4px/char) plus horizontal padding.
  const LAB_H = 17, LAB_GAP = 9;
  const endLabels = series.map((s) => {
    // Last period that actually has a value — anchors the label.
    let li = -1;
    for (let i = s.buckets.length - 1; i >= 0; i--) { if (s.buckets[i].value != null) { li = i; break; } }
    if (li < 0) return null;
    const txt = axisFmt(s.kind)(s.buckets[li].value as number);
    const bw = txt.length * 6.4 + 12;
    const py = y(s, s.buckets[li].value as number);
    // Sit above the point, flipping below only when that would leave the plot.
    const cy = py - LAB_GAP - LAB_H / 2 < padT ? py + LAB_GAP + LAB_H / 2 : py - LAB_GAP - LAB_H / 2;
    // Clamp so the pill stays inside the axes instead of being clipped.
    const cx = Math.min(Math.max(x(li), padL + bw / 2), W - padR - bw / 2);
    return { key: s.key, color: s.color, txt, bw, cx, cy };
  }).filter((l): l is NonNullable<typeof l> => l != null);
  // Series often end close together — nudge overlapping pills apart vertically
  // so every value stays readable.
  endLabels.sort((a, b) => a.cy - b.cy);
  for (let i = 1; i < endLabels.length; i++) {
    const prev = endLabels[i - 1], cur = endLabels[i];
    const overlapsX = Math.abs(cur.cx - prev.cx) < (cur.bw + prev.bw) / 2 + 2;
    if (overlapsX && cur.cy - prev.cy < LAB_H + 3) cur.cy = prev.cy + LAB_H + 3;
  }
  for (const l of endLabels) l.cy = Math.min(Math.max(l.cy, padT + LAB_H / 2), H - padB - LAB_H / 2);
  return (
    <svg ref={svgRef} width="100%" viewBox={`0 0 ${W} ${H}`} style={{ marginTop: 14, display: "block" }} onMouseMove={onMove} onMouseLeave={() => setHoverI(null)}>
      {[0, 0.25, 0.5, 0.75, 1].map((t) => {
        const yy = padT + (1 - t) * (H - padT - padB);
        return <g key={t}>
          <line x1={padL} x2={W - padR} y1={yy} y2={yy} stroke="var(--line-soft)" />
          <text x={padL - 8} y={yy + 3} textAnchor="end" fontSize="9" fill="var(--ink-3)">{left.fmt(left.min + t * left.span)}</text>
          {right && <text x={W - padR + 8} y={yy + 3} textAnchor="start" fontSize="9" fill="var(--ink-3)">{right.fmt(right.min + t * right.span)}</text>}
        </g>;
      })}
      {/* Zero baseline, drawn only on a single-axis chart that crosses zero —
          with a left AND right scale the two zeroes land at different heights,
          and one line would read as both. */}
      {!right && left.min < 0 && left.max > 0 && (
        <line x1={padL} x2={W - padR} y1={padT + (1 - (0 - left.min) / left.span) * (H - padT - padB)} y2={padT + (1 - (0 - left.min) / left.span) * (H - padT - padB)} stroke="var(--ink-4)" strokeWidth="1" />
      )}
      <line x1={padL} x2={padL} y1={padT} y2={H - padB} stroke="var(--line)" />
      <line x1={padL} x2={W - padR} y1={H - padB} y2={H - padB} stroke="var(--line)" />
      {right && <line x1={W - padR} x2={W - padR} y1={padT} y2={H - padB} stroke="var(--line)" />}
      <text x={padL - 8} y={padT - 4} textAnchor="end" fontSize="9" fontWeight="700" fill="var(--ink-3)">{unitLabel(left.kind)}</text>
      {right && <text x={W - padR + 8} y={padT - 4} textAnchor="start" fontSize="9" fontWeight="700" fill="var(--ink-3)">{unitLabel(right.kind)}</text>}
      {series.map((s) => {
        const pts = s.buckets
          .map((b, i) => (b.value == null ? null : `${x(i)},${y(s, b.value)}`))
          .filter((p): p is string => p != null);
        return (
          <g key={s.key}>
            {pts.length > 1 && <polyline points={pts.join(" ")} fill="none" stroke={s.color} strokeWidth="2.2" strokeLinejoin="round" strokeLinecap="round" />}
            {s.buckets.map((b, i) => (b.value == null ? null : <circle key={i} cx={x(i)} cy={y(s, b.value)} r="2.7" fill="#fff" stroke={s.color} strokeWidth="1.7" />))}
          </g>
        );
      })}
      {series[0]?.buckets.map((b, i) => showX(i) && <text key={i} x={x(i)} y={H - 12} textAnchor="middle" fontSize="9" fill="var(--ink-3)">{b.label}</text>)}
      {hoverI == null && endLabels.map((l) => (
        <g key={"lab" + l.key} style={{ pointerEvents: "none" }}>
          <rect x={l.cx - l.bw / 2} y={l.cy - LAB_H / 2} width={l.bw} height={LAB_H} rx="5"
            fill="var(--surface-1)" fillOpacity="0.94" stroke={l.color} strokeOpacity="0.45" strokeWidth="1" />
          <text x={l.cx} y={l.cy + 3.8} textAnchor="middle" fontSize="10.5" fontWeight="700" fill={l.color}>{l.txt}</text>
        </g>
      ))}
      {hoverI != null && (
        <g pointerEvents="none">
          <line x1={tipX} x2={tipX} y1={padT} y2={H - padB} stroke="var(--ink-3)" strokeDasharray="3 3" opacity="0.6" />
          {series.map((s) => {
            const v = s.buckets[hoverI]?.value;
            return v == null ? null : <circle key={s.key} cx={tipX} cy={y(s, v)} r="4" fill={s.color} stroke="#fff" strokeWidth="1.5" />;
          })}
          <g transform={`translate(${tipRight ? tipX - 148 : tipX + 8}, ${padT + 4})`}>
            <rect width="140" height={18 + series.length * 14} rx="4" fill="#fff" stroke="var(--line)" />
            <text x="8" y="13" fontSize="10" fontWeight="700" fill="var(--ink-1)">{series[0]?.buckets[hoverI]?.label}</text>
            {series.map((s, i) => {
              const v = s.buckets[hoverI]?.value;
              return (
                <g key={s.key} transform={`translate(8, ${28 + i * 14})`}>
                  <circle cx="4" cy="-3" r="3.5" fill={s.color} />
                  <text x="12" y="0" fontSize="10" fill="var(--ink-2)">{s.key.length > 14 ? s.key.slice(0, 13) + "…" : s.key}</text>
                  <text x="132" y="0" fontSize="10" fontWeight="700" fill="var(--ink-1)" textAnchor="end">{v == null ? "—" : axisFmt(s.kind)(v)}</text>
                </g>
              );
            })}
          </g>
        </g>
      )}
    </svg>
  );
}

export function Legend({ items }: { items: { label: string; color: string }[] }) {
  return (
    <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 10 }}>
      {items.map((it) => <span key={it.label} style={{ fontSize: 12, color: "var(--ink-2)", display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 11, height: 3, background: it.color, borderRadius: 2 }} />{it.label}</span>)}
    </div>
  );
}
